import { DateTime } from 'luxon'

/**
 * @typedef {'loading' | 'unscheduled' | 'open' | 'soon' | 'closed'} StoreHoursStatus
 */

/**
 * @typedef {{ id?: number, opens_at?: string | null, closes_at?: string | null }} StoreHourWindow
 */

const INVALID = DateTime.invalid('store hours unavailable')

const parseWindow = storeHour => ({
  source: storeHour,
  opensAt: DateTime.fromISO(storeHour?.opens_at ?? ''),
  closesAt: DateTime.fromISO(storeHour?.closes_at ?? '')
})

const covers = (parsed, now) => parsed.opensAt <= now && parsed.closesAt >= now

/**
 * Resolve o estado da lojinha a partir das janelas de funcionamento do evento
 * (`store_setting.store_hours`). Os limites são inclusivos nos dois lados, como
 * o `StoreHour#covers?` da api.
 *
 * @param {{ windows?: StoreHourWindow[] | null, now?: DateTime }} params
 * @returns {{
 *   status: StoreHoursStatus
 *   openAt: DateTime
 *   closeAt: DateTime
 *   hasSchedule: boolean
 *   currentWindow: StoreHourWindow | null
 *   nextWindow: StoreHourWindow | null
 * }}
 */
export function resolveStoreHours({ windows, now = DateTime.now() }) {
  const storeHours = Array.isArray(windows) ? windows : []
  const hasSchedule = storeHours.length > 0

  if (!hasSchedule) {
    return {
      status: 'unscheduled',
      openAt: INVALID,
      closeAt: INVALID,
      hasSchedule: false,
      currentWindow: null,
      nextWindow: null
    }
  }

  const parsed = storeHours.map(parseWindow)

  if (parsed.some(({ opensAt, closesAt }) => !opensAt.isValid || !closesAt.isValid)) {
    return {
      status: 'loading',
      openAt: INVALID,
      closeAt: INVALID,
      hasSchedule: true,
      currentWindow: null,
      nextWindow: null
    }
  }

  const sorted = [...parsed].sort((a, b) => a.opensAt - b.opensAt)
  const current = sorted.find(storeHour => covers(storeHour, now))
  const upcoming = sorted.find(storeHour => storeHour.opensAt > now)

  if (current) {
    return {
      status: 'open',
      openAt: current.opensAt,
      closeAt: current.closesAt,
      hasSchedule: true,
      currentWindow: current.source,
      nextWindow: upcoming?.source ?? null
    }
  }

  if (upcoming) {
    return {
      status: 'soon',
      openAt: upcoming.opensAt,
      closeAt: upcoming.closesAt,
      hasSchedule: true,
      currentWindow: null,
      nextWindow: upcoming.source
    }
  }

  const last = sorted[sorted.length - 1]

  return {
    status: 'closed',
    openAt: last.opensAt,
    closeAt: last.closesAt,
    hasSchedule: true,
    currentWindow: null,
    nextWindow: null
  }
}

/**
 * A vitrine é navegável fora da janela: quem chega antes da abertura continua
 * vendo o que vai poder trocar. O que barra o resgate é a elegibilidade que a
 * api devolve item a item, com o motivo no card e no rodapé — esconder o
 * catálogo tirava a única resposta para "o que tem aqui?".
 *
 * Carregando é o único estado sem catálogo, e mesmo assim só para quem não
 * administra: sem janela resolvida não há o que prometer.
 */
export function canRenderStoreCatalog(status, { canManageAll = false } = {}) {
  if (status === 'loading') return canManageAll

  return true
}

/**
 * O resgate é possível? Diferente de `canRenderStoreCatalog`, que ficou
 * permissivo de propósito — a vitrine é navegável fora da janela.
 *
 * `unscheduled` entra como fechado porque é o fail-safe da RN-13: evento sem
 * nenhuma janela mantém a lojinha fechada, e a api recusa o resgate com
 * `reward_shop_closed`. Deixar o botão ativo ali prometeria o que o servidor
 * nega.
 *
 * `loading` fica de fora da recusa: enquanto as janelas não resolveram, rotular
 * a loja como fechada erraria em metade dos casos, e o servidor ainda é a
 * palavra final — quem clicar cedo demais recebe o aviso vindo dele.
 *
 * @param {StoreHoursStatus} status
 * @returns {boolean}
 */
export function canRedeemFromStore(status) {
  return status === 'open' || status === 'loading'
}

/**
 * Horário de fechamento como o Participante lê, em todas as superfícies de
 * prazo. `null` quando não há janela — quem chama decide não exibir nada.
 *
 * @param {DateTime | null | undefined} closeAt
 * @returns {string | null}
 */
export function formatStoreCloseTime(closeAt) {
  return closeAt?.isValid ? closeAt.toFormat("HH'h'mm") : null
}

/**
 * O dia do fechamento, e só quando ele não é hoje — dizer "hoje" em toda janela
 * do dia é ruído numa faixa que trunca em uma linha.
 *
 * @param {DateTime | null | undefined} closeAt
 * @param {DateTime} [now]
 * @returns {string | null}
 */
export function formatStoreCloseDay(closeAt, now = DateTime.now()) {
  if (!closeAt?.isValid) return null
  if (closeAt.hasSame(now, 'day')) return null

  return closeAt.setLocale('pt-BR').toFormat("cccc, d 'de' LLLL")
}
